/**
 * <s-recommendation-order> — puts the product carousel's cards in Shopify's "related" recommendation
 * order for the product being viewed (most to least likely purchase: built from purchase history,
 * then similar descriptions, then related collections), without changing the card markup.
 *
 * The carousel is server-rendered in Best Sellers order, so it works without JS or if the request
 * fails. On connect this fetches the Product Recommendations JSON endpoint, moves recommended cards
 * to the front in that order (everything else keeps its Best Sellers order after them), and refills
 * the existing pages of four so the page count, arrows and dots stay as they are.
 *
 * Attributes:
 *   data-url         — routes.product_recommendations_url (Liquid), never hard-coded.
 *   data-product-id  — the viewed product's id.
 *   data-limit       — how many recommendations to ask for (max 10 per the endpoint).
 * Expected DOM: pages marked [data-rec-page], each card wrapper marked [data-rec-handle].
 */
class SRecommendationOrder extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#reorder(this.#ac.signal).catch(() => {});
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #reorder(signal) {
    const { url, productId, limit = '10' } = this.dataset;
    if (!url || !productId) return;

    const endpoint = new URL(`${url}.json`, location.origin);
    endpoint.searchParams.set('product_id', productId);
    endpoint.searchParams.set('limit', limit);
    endpoint.searchParams.set('intent', 'related');

    const response = await fetch(endpoint, { signal, headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const data = await response.json();
    const ranked = (data.products || []).map((product) => product.handle);
    if (!ranked.length || !this.isConnected) return;

    const pages = [...this.querySelectorAll('[data-rec-page]')];
    const cards = pages.flatMap((page) => [...page.querySelectorAll(':scope > [data-rec-handle]')]);
    if (!cards.length) return;

    const rank = (card) => {
      const index = ranked.indexOf(card.dataset.recHandle);
      return index === -1 ? ranked.length : index;
    };
    // Array.prototype.sort is stable, so unranked cards keep their Best Sellers order.
    const ordered = cards.slice().sort((a, b) => rank(a) - rank(b));
    if (ordered.every((card, index) => card === cards[index])) return;

    const sizes = pages.map((page) => page.querySelectorAll(':scope > [data-rec-handle]').length);
    let next = 0;
    pages.forEach((page, pageIndex) => {
      const slice = ordered.slice(next, next + sizes[pageIndex]);
      next += sizes[pageIndex];
      page.replaceChildren(...slice);
    });
  }
}

if (!customElements.get('s-recommendation-order')) {
  customElements.define('s-recommendation-order', SRecommendationOrder);
}
